import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CalendarClock,
  Check,
  Clock,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  Truck,
  Wrench,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { maintenanceApi } from '@/api/endpoints/maintenance';
import { permissionsApi } from '@/api/endpoints/permissions';
import { useFleetStore } from '@/stores/fleet';
import { cn } from '@/lib/utils';
import type { Maintenance } from '@/api/types';
import { mensagemDeErro } from '@/lib/apiError';

type TypeKey = 'engineHours' | 'days' | 'months';

interface TypeSpec {
  key: TypeKey;
  label: string;
  description: string;
  unitShort: string;
  icon: typeof Clock;
}

const TYPES: TypeSpec[] = [
  {
    key: 'engineHours',
    label: 'Horas trabalhadas',
    description: 'Alerta a cada N horas trabalhadas pela embarcação',
    unitShort: 'h',
    icon: Clock,
  },
  {
    key: 'days',
    label: 'Dias',
    description: 'Alerta a cada N dias corridos',
    unitShort: 'dias',
    icon: Calendar,
  },
  {
    key: 'months',
    label: 'Meses',
    description: 'Alerta a cada N meses corridos',
    unitShort: 'meses',
    icon: CalendarClock,
  },
];

function specForType(type?: string): TypeSpec {
  return TYPES.find((t) => t.key === type) ?? TYPES[0];
}

/** Atalhos de manutenção náutica comuns (preenchem o nome). */
const NAUTICAL_PRESETS = [
  'Troca de óleo',
  'Revisão do motor',
  'Revisão elétrica',
  'Revisão de bateria',
  'Bomba de porão',
  'Revisão do casco',
  'Carreta/reboque',
  'Limpeza de casco',
];

const HOURS_MS = 3_600_000;
const DAY_MS = 24 * HOURS_MS;
const MONTH_MS_APPROX = 30 * DAY_MS;

interface ScheduleAttrs {
  deviceId?: number;
  intervalDays?: number;
  intervalMonths?: number;
}

function readAttrs(m: Maintenance): ScheduleAttrs {
  const a = m.attributes ?? {};
  return {
    deviceId: typeof a.deviceId === 'number' ? (a.deviceId as number) : undefined,
    intervalDays: typeof a.intervalDays === 'number' ? (a.intervalDays as number) : undefined,
    intervalMonths: typeof a.intervalMonths === 'number' ? (a.intervalMonths as number) : undefined,
  };
}

function formatPeriod(m: Maintenance): { start: string | null; period: string } {
  const spec = specForType(m.type);
  const attrs = readAttrs(m);
  if (spec.key === 'engineHours') {
    return {
      start: ((m.start ?? 0) / HOURS_MS).toLocaleString('pt-BR', { maximumFractionDigits: 2 }),
      period: ((m.period ?? 0) / HOURS_MS).toLocaleString('pt-BR', { maximumFractionDigits: 2 }),
    };
  }
  if (spec.key === 'days') {
    const days = attrs.intervalDays ?? Math.round((m.period ?? 0) / DAY_MS);
    return { start: null, period: days.toLocaleString('pt-BR') };
  }
  const months = attrs.intervalMonths ?? Math.round((m.period ?? 0) / MONTH_MS_APPROX);
  return { start: null, period: months.toLocaleString('pt-BR') };
}

export function MaintenancePage() {
  // Sem `all=true`: o Traccar retorna 400 se o usuário logado não tiver
  // privilégio de admin/manager. Pega só as manutenções do usuário.
  const list = useQuery({
    queryKey: ['maintenance'],
    queryFn: () => maintenanceApi.list(),
  });
  const [editing, setEditing] = useState<Maintenance | 'new' | null>(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const data = list.data ?? [];
    if (!q) return data;
    return data.filter(
      (m) => m.name?.toLowerCase().includes(q) || specForType(m.type).label.toLowerCase().includes(q),
    );
  }, [list.data, search]);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Manutenção</h1>
          <p className="text-sm text-muted-foreground">
            Alertas por embarcação por horas trabalhadas, dias ou meses.
          </p>
        </div>
        <Button onClick={() => setEditing('new')} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Nova manutenção
        </Button>
      </div>

      <PendenciasDashboard />

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou tipo"
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      {list.isLoading ? (
        <p className="p-6 text-sm text-muted-foreground">Carregando manutenções…</p>
      ) : list.error ? (
        <p className="p-6 text-sm text-destructive">Erro ao carregar manutenções.</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Wrench className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {search
                ? 'Nenhuma manutenção corresponde à busca.'
                : 'Nenhuma manutenção cadastrada.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((m) => (
            <MaintenanceCard key={m.id} maintenance={m} onEdit={() => setEditing(m)} />
          ))}
        </div>
      )}

      {editing && (
        <MaintenanceEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

interface Pendencia {
  deviceId: number;
  deviceName: string;
  label: string;
  date: string;
  days: number;
}

/** Dashboard de pendências: vencimentos de seguro/documento das embarcações
 *  (cadastrados em Documentos) vencidos ou nos próximos 30 dias. */
function PendenciasDashboard() {
  const devices = useFleetStore((s) => s.devices);

  const pendencias = useMemo<Pendencia[]>(() => {
    const out: Pendencia[] = [];
    const now = Date.now();
    for (const d of devices.values()) {
      const a = d.attributes ?? {};
      const checks: [string, unknown][] = [
        ['Seguro', a.vencSeguro],
        ['Documento', a.vencDoc],
      ];
      for (const [label, raw] of checks) {
        if (typeof raw !== 'string' || !raw.trim()) continue;
        const target = new Date(`${raw}T23:59:59`).getTime();
        if (Number.isNaN(target)) continue;
        const days = Math.floor((target - now) / DAY_MS);
        if (days <= 30) out.push({ deviceId: d.id, deviceName: d.name, label, date: raw, days });
      }
    }
    return out.sort((x, y) => x.days - y.days);
  }, [devices]);

  const vencidos = pendencias.filter((p) => p.days < 0).length;
  const proximos = pendencias.length - vencidos;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-bold">
            <CalendarClock className="h-4 w-4 text-brand" />
            Pendências de vencimento
          </h2>
          <div className="flex gap-2 text-[11px] font-bold">
            <span className="rounded-full bg-status-blocked/10 px-2 py-0.5 text-status-blocked">{vencidos} vencidos</span>
            <span className="rounded-full bg-status-ready/10 px-2 py-0.5 text-status-ready">{proximos} próximos</span>
          </div>
        </div>

        {pendencias.length === 0 ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-status-running" />
            Nenhum seguro/documento vencido ou a vencer nos próximos 30 dias.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {pendencias.slice(0, 12).map((p) => {
              const vencido = p.days < 0;
              return (
                <li key={`${p.deviceId}-${p.label}`}>
                  <Link
                    to={`/devices/${p.deviceId}`}
                    className="flex items-center gap-3 py-2 hover:opacity-80"
                  >
                    <span
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                        vencido ? 'bg-status-blocked/10 text-status-blocked' : 'bg-status-ready/10 text-status-ready',
                      )}
                    >
                      <AlertTriangle className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{p.deviceName}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.label} · {new Date(`${p.date}T00:00:00`).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold',
                        vencido ? 'bg-status-blocked/10 text-status-blocked' : 'bg-status-ready/10 text-status-ready',
                      )}
                    >
                      {vencido ? `Vencido há ${-p.days}d` : `Vence em ${p.days}d`}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function MaintenanceCard({
  maintenance,
  onEdit,
}: {
  maintenance: Maintenance;
  onEdit: () => void;
}) {
  const spec = specForType(maintenance.type);
  const Icon = spec.icon;
  const attrs = readAttrs(maintenance);
  const devices = useFleetStore((s) => s.devices);
  const device = attrs.deviceId ? devices.get(attrs.deviceId) : undefined;
  const period = formatPeriod(maintenance);

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-status-running/10 text-status-running">
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold">{maintenance.name}</p>
              <p className="truncate text-xs text-muted-foreground">{spec.label}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
            aria-label="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>

        {device ? (
          <p className="flex items-center gap-1.5 text-xs">
            <Truck className="h-3.5 w-3.5 text-primary" />
            <span className="truncate font-semibold">{device.name}</span>
          </p>
        ) : attrs.deviceId ? (
          <p className="text-xs text-muted-foreground">Embarcação ID {attrs.deviceId}</p>
        ) : (
          <p className="text-xs text-muted-foreground">Sem embarcação vinculada</p>
        )}

        <dl className={cn('grid gap-1 text-xs', period.start !== null ? 'grid-cols-2' : 'grid-cols-1')}>
          {period.start !== null && (
            <div>
              <dt className="text-muted-foreground">Início</dt>
              <dd className="font-semibold">
                {period.start} {spec.unitShort}
              </dd>
            </div>
          )}
          <div>
            <dt className="text-muted-foreground">A cada</dt>
            <dd className="font-semibold">
              {period.period} {spec.unitShort}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

interface FormState {
  name: string;
  typeKey: TypeKey;
  deviceId: number | null;
  hoursStart: string;
  hoursPeriod: string;
  daysPeriod: string;
  monthsPeriod: string;
}

function fromMaintenance(m: Maintenance): FormState {
  const spec = specForType(m.type);
  const attrs = readAttrs(m);
  return {
    name: m.name ?? '',
    typeKey: spec.key,
    deviceId: attrs.deviceId ?? null,
    hoursStart: ((m.start ?? 0) / HOURS_MS).toString(),
    hoursPeriod: ((m.period ?? 0) / HOURS_MS).toString(),
    daysPeriod: (attrs.intervalDays ?? Math.round((m.period ?? 0) / DAY_MS) ?? 0).toString(),
    monthsPeriod: (
      attrs.intervalMonths ?? Math.round((m.period ?? 0) / MONTH_MS_APPROX) ?? 0
    ).toString(),
  };
}

function MaintenanceEditor({
  initial,
  onClose,
}: {
  initial: Maintenance | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const devices = useFleetStore((s) => s.devices);
  const sortedDevices = useMemo(
    () => Array.from(devices.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [devices],
  );

  const [form, setForm] = useState<FormState>(
    initial
      ? fromMaintenance(initial)
      : {
          name: '',
          typeKey: 'engineHours',
          deviceId: null,
          hoursStart: '0',
          hoursPeriod: '250',
          daysPeriod: '30',
          monthsPeriod: '6',
        },
  );
  const [touched, setTouched] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState('');

  useEffect(() => {
    if (initial) setForm(fromMaintenance(initial));
  }, [initial]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const filteredDevices = useMemo(() => {
    const q = deviceSearch.trim().toLowerCase();
    if (!q) return sortedDevices;
    return sortedDevices.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (d.model ?? '').toLowerCase().includes(q) ||
        (d.uniqueId ?? '').toLowerCase().includes(q),
    );
  }, [sortedDevices, deviceSearch]);

  const errors = useMemo(() => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) e.name = 'Obrigatório';
    if (!form.deviceId) e.deviceId = 'Escolha a embarcação';
    if (form.typeKey === 'engineHours') {
      const periodNum = Number(form.hoursPeriod);
      if (!Number.isFinite(periodNum) || periodNum <= 0) e.hoursPeriod = 'Maior que zero';
      const startNum = Number(form.hoursStart);
      if (!Number.isFinite(startNum) || startNum < 0) e.hoursStart = 'Maior ou igual a zero';
    } else if (form.typeKey === 'days') {
      const n = Number(form.daysPeriod);
      if (!Number.isFinite(n) || n <= 0) e.daysPeriod = 'Maior que zero';
    } else {
      const n = Number(form.monthsPeriod);
      if (!Number.isFinite(n) || n <= 0) e.monthsPeriod = 'Maior que zero';
    }
    return e;
  }, [form]);

  const valid = Object.keys(errors).length === 0;

  const buildPayload = (): Maintenance => {
    const baseAttrs = (initial?.attributes ?? {}) as Record<string, unknown>;
    const attributes: Record<string, unknown> = {
      ...baseAttrs,
      deviceId: form.deviceId,
    };
    delete attributes.scheduleDate;
    delete attributes.recurrenceDays;
    delete attributes.intervalDays;
    delete attributes.intervalMonths;

    let start = 0;
    let period = 0;

    if (form.typeKey === 'engineHours') {
      start = Number(form.hoursStart) * HOURS_MS;
      period = Number(form.hoursPeriod) * HOURS_MS;
    } else if (form.typeKey === 'days') {
      const n = Number(form.daysPeriod);
      attributes.intervalDays = n;
      period = n * DAY_MS;
    } else {
      const n = Number(form.monthsPeriod);
      attributes.intervalMonths = n;
      period = n * MONTH_MS_APPROX;
    }

    return {
      ...(initial ?? { id: 0 }),
      name: form.name.trim(),
      type: form.typeKey,
      start,
      period,
      attributes,
    } as Maintenance;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      let saved: Maintenance;
      if (initial) {
        saved = await maintenanceApi.update(initial.id, payload);
      } else {
        const { id, ...rest } = payload;
        void id;
        saved = await maintenanceApi.create(rest);
      }

      if (saved.id && form.deviceId) {
        try {
          await permissionsApi.link({ maintenanceId: saved.id, deviceId: form.deviceId });
        } catch (e) {
          console.warn('Vínculo manutenção/embarcação:', e);
        }
      }
      return saved;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => maintenanceApi.delete(initial!.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      onClose();
    },
  });

  const errorText = mensagemDeErro;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    saveMutation.mutate();
  };

  const onConfirmDelete = () => {
    if (!initial) return;
    if (!window.confirm(`Excluir a manutenção "${initial.name}"?`)) return;
    deleteMutation.mutate();
  };

  const selectedDevice = form.deviceId ? devices.get(form.deviceId) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-2 sm:p-6"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="flex max-h-[95vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-bold">
            {initial ? 'Editar manutenção' : 'Nova manutenção'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <Field label="Nome" required error={touched ? errors.name : undefined}>
            <Input
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Ex: nome da manutenção"
              autoFocus
            />
            <div className="mt-1.5 flex flex-wrap gap-1">
              {NAUTICAL_PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => update('name', p)}
                  className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-accent"
                >
                  {p}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Embarcação" required error={touched ? errors.deviceId : undefined}>
            <div className="space-y-1.5">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={deviceSearch}
                  onChange={(e) => setDeviceSearch(e.target.value)}
                  placeholder={
                    selectedDevice
                      ? `Selecionada: ${selectedDevice.name} — digite para filtrar`
                      : 'Buscar embarcação por nome, modelo ou IMEI'
                  }
                  className="pl-9"
                />
              </div>
              <select
                value={form.deviceId ?? ''}
                onChange={(e) => update('deviceId', e.target.value ? Number(e.target.value) : null)}
                size={Math.min(6, Math.max(3, filteredDevices.length))}
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
              >
                {filteredDevices.length === 0 && (
                  <option value="" disabled>
                    Nenhuma embarcação corresponde
                  </option>
                )}
                {filteredDevices.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                    {d.model ? ` · ${d.model}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </Field>

          <Field label="Tipo de medição">
            <div className="grid grid-cols-3 gap-1.5">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => update('typeKey', t.key)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-[11px] font-semibold transition-colors',
                    form.typeKey === t.key
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-accent',
                  )}
                >
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">
              {specForType(form.typeKey).description}
            </p>
          </Field>

          {form.typeKey === 'engineHours' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Início (h)" error={touched ? errors.hoursStart : undefined}>
                <Input
                  type="number"
                  min={0}
                  step="any"
                  value={form.hoursStart}
                  onChange={(e) => update('hoursStart', e.target.value)}
                />
              </Field>
              <Field label="A cada (h)" required error={touched ? errors.hoursPeriod : undefined}>
                <Input
                  type="number"
                  min={0}
                  step="any"
                  value={form.hoursPeriod}
                  onChange={(e) => update('hoursPeriod', e.target.value)}
                />
              </Field>
            </div>
          )}

          {form.typeKey === 'days' && (
            <Field label="A cada (dias)" required error={touched ? errors.daysPeriod : undefined}>
              <Input
                type="number"
                min={1}
                step={1}
                value={form.daysPeriod}
                onChange={(e) => update('daysPeriod', e.target.value)}
              />
            </Field>
          )}

          {form.typeKey === 'months' && (
            <Field label="A cada (meses)" required error={touched ? errors.monthsPeriod : undefined}>
              <Input
                type="number"
                min={1}
                step={1}
                value={form.monthsPeriod}
                onChange={(e) => update('monthsPeriod', e.target.value)}
              />
            </Field>
          )}

          {(form.typeKey === 'days' || form.typeKey === 'months') && (
            <p className="rounded-md bg-status-ready/10 px-3 py-2 text-[11px] text-status-ready">
              Tipos por tempo são lembretes do painel; o servidor só dispara evento automático
              pelo acumulador de horas trabalhadas.
            </p>
          )}

          {(saveMutation.error || deleteMutation.error) && (
            <p className="flex items-start gap-2 rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {errorText(saveMutation.error ?? deleteMutation.error)}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
          {initial && (
            <Button
              type="button"
              variant="destructive"
              onClick={onConfirmDelete}
              disabled={deleteMutation.isPending || saveMutation.isPending}
              className="mr-auto gap-1.5"
            >
              <Trash2 className="h-4 w-4" />
              {deleteMutation.isPending ? 'Excluindo…' : 'Excluir'}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!valid || saveMutation.isPending} className="gap-1.5">
            <Check className="h-4 w-4" />
            {saveMutation.isPending ? 'Salvando…' : initial ? 'Salvar' : 'Criar'}
          </Button>
        </footer>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>
          {label}
          {required && <span className="text-status-blocked"> *</span>}
        </span>
        {error && <span className="text-status-blocked">{error}</span>}
      </span>
      <div className={cn(error && 'rounded-md ring-2 ring-status-blocked/30')}>{children}</div>
    </label>
  );
}
